import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Clock, Instagram, Facebook } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";

const LEGACY_SEMINAR_SLUG = "organisez-vos-seminaires-dentreprise-a-ville-davray-dans-un-cadre-dexception";

export default function Footer() {
  const [seoPages, setSeoPages] = useState([]);  const { t, lang, localePath } = useI18n();

  useEffect(() => {
    api.get("/seo-pages/public").then((r) => setSeoPages(r.data || [])).catch(() => {});
  }, []);

  const NAV_KEYS = [
    { key: "home", path: "/" },
    { key: "menu", path: "/menu" },
    { key: "events", path: "/evenements" },
    { key: "gallery", path: "/galerie" },
    { key: "giftcard", path: "/carte-cadeau" },
    { key: "contact", path: "/contact" },
  ];

  return (
    <footer data-testid="public-footer" className="bg-[#203240] text-white mt-32">
      <div className="max-w-[1500px] mx-auto px-6 md:px-12 lg:px-16 py-20 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-4">
          <Link to={localePath("/")} className="inline-block">
            <img
              src="/logo.webp"
              alt="L'Entracte — Restaurant & Réception"
              className="h-20 md:h-24 w-auto brightness-0 invert opacity-95"
            />
          </Link>
          <p className="mt-6 text-white/70 leading-relaxed max-w-md text-sm">
            {t("footer.tagline")}
          </p>
        </div>

        <div className="md:col-span-3">
          <p className="overline text-[#B8956A]">{t("contact.address")}</p>
          <div className="mt-5 space-y-3">
            <div className="flex items-start gap-3 text-sm">
              <MapPin size={16} className="text-[#B8956A] mt-1" />
              <span>3 rue de Versailles<br/>92410 Ville d'Avray</span>
            </div>
            <div className="flex items-start gap-3 text-sm">
              <Phone size={16} className="text-[#B8956A] mt-1" />
              <a href="tel:+33182001867" className="link-gold">01 82 00 18 67</a>
            </div>
            <div className="flex items-start gap-3 text-sm">
              <Clock size={16} className="text-[#B8956A] mt-1" />
              <div>
                <div>{t("footer.hoursValues.tueSat").split(" · ")[0]}</div>
                <div className="text-white/60">{t("footer.hoursValues.tueSat").split(" · ").slice(1).join(" · ")}</div>
                <div className="mt-1">{t("footer.hoursValues.sunday").split(" · ")[0]}</div>
                <div className="text-white/60">{t("footer.hoursValues.sunday").split(" · ")[1]}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="md:col-span-2">
          <p className="overline text-[#B8956A]">{t("footer.sections")}</p>
          <ul className="mt-5 space-y-2 text-sm">
            {NAV_KEYS.map((n) => (
              <li key={n.key}>
                <Link to={localePath(n.path)} className="text-white/80 hover:text-[#B8956A] transition-colors">
                  {t(`nav.${n.key}`)}
                </Link>
              </li>
            ))}
          </ul>
          <p className="overline text-[#B8956A] mt-8">{lang === "en" ? "Follow us" : "Suivez-nous"}</p>
          <div className="mt-5 flex gap-3">
            <a href="https://www.instagram.com/lentracte_villedavray/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-10 h-10 border border-white/20 flex items-center justify-center hover:border-[#B8956A] hover:text-[#B8956A] text-white transition-colors"><Instagram size={16} /></a>
            <a href="https://www.facebook.com/LentracteVilledavray/" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-10 h-10 border border-white/20 flex items-center justify-center hover:border-[#B8956A] hover:text-[#B8956A] text-white transition-colors"><Facebook size={16} /></a>
          </div>
          <Link to="/admin/login" data-testid="footer-admin-link" className="mt-8 inline-block text-xs text-white/50 hover:text-[#B8956A] tracking-widest uppercase">
            {lang === "en" ? "Pro area" : "Espace pro"}
          </Link>
        </div>

        <div className="md:col-span-3">
          <p className="overline text-[#B8956A]">{lang === "en" ? "Events" : "Événements"}</p>
          <ul className="mt-5 space-y-2 text-sm" data-testid="footer-event-pages">
            {[
              { to: "/mariage", fr: "Mariage", en: "Wedding" },
              { to: "/seminaire", fr: "Séminaire d'entreprise", en: "Corporate seminar" },
              { to: "/bapteme", fr: "Baptême & communion", en: "Christening & communion" },
              { to: "/anniversaire", fr: "Anniversaire & fête de famille", en: "Birthday & family celebration" },
              { to: "/mariage-halal-ville-davray", fr: "Mariage halal", en: "Halal wedding" },
              { to: "/mariage-juif-ville-davray", fr: "Mariage juif", en: "Jewish wedding" },
            ].map((l) => (
              <li key={l.to}>
                <Link
                  to={localePath(l.to)}
                  data-testid={`footer-event-${l.to.replace("/", "")}`}
                  className="text-white/80 hover:text-[#B8956A] transition-colors"
                >
                  {lang === "en" ? l.en : l.fr}
                </Link>
              </li>
            ))}
          </ul>
          <p className="overline text-[#B8956A] mt-8">{lang === "en" ? "Our services" : "Nos services"}</p>
          <ul className="mt-5 space-y-2 text-sm" data-testid="footer-seo-pages">
            {seoPages
              .filter((p) => p.slug !== LEGACY_SEMINAR_SLUG)
              .map((p) => (
              <li key={p.id}>
                <Link to={localePath(`/${p.slug}`)} className="text-white/80 hover:text-[#B8956A] transition-colors leading-snug block" data-testid={`footer-seo-${p.slug}`}>
                  {p.title.split(" | ")[0].split(" : ")[0].replace(/^Organisez vos /, "").replace(/^Votre /, "")}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 py-6 px-6 md:px-12 lg:px-16 max-w-[1500px] mx-auto flex flex-col sm:flex-row gap-3 justify-between items-center text-xs text-white/50">
        <p>© {new Date().getFullYear()} {t("footer.rights").replace("© ", "")}</p>
        <div className="flex gap-4 flex-wrap justify-center">
          <Link to={localePath("/mentions-legales")} className="hover:text-[#B8956A] transition-colors">{t("footer.legalLinks.mentions")}</Link>
          <Link to={localePath("/politique-de-confidentialite")} className="hover:text-[#B8956A] transition-colors">{t("footer.legalLinks.privacy")}</Link>
          <Link to={localePath("/cookies")} className="hover:text-[#B8956A] transition-colors">{t("footer.legalLinks.cookies")}</Link>
        </div>
      </div>
    </footer>
  );
}
